/**
 * Dev-only auth-bypass header plumbing (security review P10b).
 *
 * The backend development auth bypass no longer sniffs the User-Agent; it now
 * requires the `X-Dev-Bypass-Secret` request header to match the backend's
 * `DEV_BYPASS_SECRET` (constant-time, fail-closed). When the web SPA runs with
 * the mock-Clerk bypass active (`VITE_DEV_BYPASS_AUTH=true` in a non-production
 * Vite mode), every API request it issues must carry this header or the backend
 * will reject it with 401. The browser-driven Playwright/e2e flows depend on
 * this.
 *
 * This module is a no-op in production: `VITE_DEV_BYPASS_AUTH` is never `'true'`
 * in production builds (vite.config.ts throws if it is), so `devBypassHeaders()`
 * returns `{}` and `configureDevBypassApiHeaders()` does nothing.
 */
import { OpenAPI } from "@/api/core/OpenAPI";

/**
 * The dev-bypass request header. Must stay in sync with the backend gate
 * (`internal/middleware/clerk_jwt.go` `DevBypassSecretHeader`) and the CORS
 * allow-list (`cmd/api/main.go` `corsAllowHeaders`).
 */
export const DEV_BYPASS_HEADER = "X-Dev-Bypass-Secret";

/** True when the mock-Clerk dev auth bypass is active for this SPA build. */
export const isDevBypassActive = (): boolean =>
  import.meta.env.VITE_DEV_BYPASS_AUTH === "true";

const devBypassSecret = (): string =>
  (import.meta.env.VITE_DEV_BYPASS_SECRET as string | undefined) ?? "";

/**
 * Returns the dev-bypass header (or an empty object when the bypass is
 * inactive). Spread into a manual `fetch`/axios `headers` object so the request
 * authenticates against the backend secret gate.
 */
export const devBypassHeaders = (): Record<string, string> =>
  isDevBypassActive() ? { [DEV_BYPASS_HEADER]: devBypassSecret() } : {};

let fetchWrapped = false;

/**
 * Installs the dev-bypass secret on every API transport the SPA uses, so each
 * request authenticates against the backend secret gate. Call once at bootstrap.
 * No-op when the bypass is inactive (and therefore in production).
 *
 * Covers:
 *  - the generated OpenAPI client (axios) via `OpenAPI.HEADERS`, and
 *  - the hand-written `fetch`-based hooks (useChildren, useContacts, …) via a
 *    thin `window.fetch` wrapper that adds the header to requests aimed at the
 *    backend API origin (never to third-party hosts). These hooks build their
 *    headers manually and do not read `OpenAPI.HEADERS`, so the wrapper is the
 *    single dev-only point that covers them all.
 *
 * (The shared axios instance in `src/api/axios.ts` adds the header in its own
 * request interceptor via `devBypassHeaders()`.)
 */
export const configureDevBypassApiHeaders = (): void => {
  if (!isDevBypassActive()) {
    return;
  }

  const secret = devBypassSecret();
  // Merge rather than overwrite so any headers configured elsewhere survive.
  OpenAPI.HEADERS = { ...OpenAPI.HEADERS, [DEV_BYPASS_HEADER]: secret };

  if (fetchWrapped || typeof window === "undefined" || !window.fetch) {
    return;
  }
  fetchWrapped = true;
  // Only attach the secret to requests aimed at our backend API origin, so the
  // dev-bypass secret never leaks to third-party hosts the SPA may call.
  const apiOrigin = (() => {
    try {
      return new URL(OpenAPI.BASE, window.location.origin).origin;
    } catch {
      return window.location.origin;
    }
  })();
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let targetsApi = false;
    try {
      const reqUrl = input instanceof Request ? input.url : input.toString();
      targetsApi = new URL(reqUrl, window.location.origin).origin === apiOrigin;
    } catch {
      targetsApi = false;
    }
    if (!targetsApi) {
      return originalFetch(input, init);
    }
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    headers.set(DEV_BYPASS_HEADER, secret);
    return originalFetch(input, { ...(init ?? {}), headers });
  };
};
